import { Component } from '@theme/component';

const PAGE_SIZE = 12;

/**
 * Client-side filterable/sortable/paginated customer reviews list.
 * Review data is embedded as JSON in a child <script type="application/json">.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list
 * @property {HTMLButtonElement} loadMore
 * @property {HTMLSelectElement} sort
 * @property {HTMLElement} data
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} resultCount
 *
 * @extends {Component<Refs>}
 */
const AVATAR_COLORS = ['#4A7FC1', '#D9534F', '#3FA34D', '#E0932A', '#8B5CF6', '#1E9E9E', '#C2547A'];

/** @param {string} name */
function avatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/** @param {string} name */
function initials(name) {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export class CustomerReviews extends Component {
  /** @type {Array<{name: string, rating: number, text: string, date: string}>} */
  #allReviews = [];
  #filterRating = 0; // 0 = all
  #sortMode = 'featured';
  #visibleCount = PAGE_SIZE;
  #expanded = new Set();

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#allReviews = JSON.parse(this.refs.data.textContent || '[]');
    } catch (e) {
      this.#allReviews = [];
    }

    this.#render();
  }

  /** @param {PointerEvent & {target: HTMLElement}} event */
  filter(event) {
    const button = event.target.closest('[data-rating]');
    if (!button) return;

    this.#filterRating = Number(button.dataset.rating) || 0;
    this.#visibleCount = PAGE_SIZE;

    this.querySelectorAll('[data-rating]').forEach((btn) => {
      btn.setAttribute('aria-current', String(btn === button));
    });

    this.#render();
  }

  sort() {
    this.#sortMode = this.refs.sort.value;
    this.#visibleCount = PAGE_SIZE;
    this.#render();
  }

  loadMore() {
    this.#visibleCount += PAGE_SIZE;
    this.#render();
  }

  /** @param {PointerEvent & {target: HTMLElement}} event */
  toggleReview(event) {
    const button = event.target.closest('[data-review-index]');
    if (!button) return;

    const index = Number(button.dataset.reviewIndex);
    if (this.#expanded.has(index)) {
      this.#expanded.delete(index);
    } else {
      this.#expanded.add(index);
    }
    this.#render();
  }

  #getFilteredSorted() {
    let items = this.#allReviews;

    if (this.#filterRating > 0) {
      items = items.filter((review) => review.rating === this.#filterRating);
    }

    items = items.slice();

    if (this.#sortMode === 'highest') {
      items.sort((a, b) => b.rating - a.rating);
    } else if (this.#sortMode === 'lowest') {
      items.sort((a, b) => a.rating - b.rating);
    }

    return items;
  }

  #renderStars(rating) {
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      stars += `<svg class="customer-reviews__star${i <= rating ? ' customer-reviews__star--filled' : ''}" viewBox="0 0 32 32" aria-hidden="true"><path d="M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z"/></svg>`;
    }
    return stars;
  }

  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  #render() {
    const filtered = this.#getFilteredSorted();
    const visible = filtered.slice(0, this.#visibleCount);

    this.refs.list.innerHTML = visible
      .map((review) => {
        const index = this.#allReviews.indexOf(review);
        const isExpanded = this.#expanded.has(index);
        const isLong = review.text.length > 220;

        return `
        <div class="customer-reviews__card">
          <div class="customer-reviews__meta">
            <span class="customer-reviews__avatar" style="background-color: ${avatarColor(review.name)};">${initials(review.name)}</span>
            <div class="customer-reviews__meta-text">
              <div class="customer-reviews__name-row">
                <p class="customer-reviews__name">${this.#escapeHtml(review.name)}</p>
                <span class="customer-reviews__verified">✓ Verified</span>
              </div>
              <span class="customer-reviews__date">${this.#escapeHtml(review.date ?? '')}</span>
            </div>
          </div>
          <div class="customer-reviews__stars">${this.#renderStars(review.rating)}</div>
          <p class="customer-reviews__text" data-truncated="${isLong && !isExpanded}">${this.#escapeHtml(review.text)}</p>
          ${
            isLong
              ? `<button type="button" class="customer-reviews__read-more" data-review-index="${index}" on:click="/toggleReview">${isExpanded ? 'Read less' : 'Read more'}</button>`
              : ''
          }
        </div>
      `;
      })
      .join('');

    if (this.refs.resultCount) {
      this.refs.resultCount.textContent = `Showing ${visible.length} of ${filtered.length} reviews`;
    }

    if (this.refs.emptyState) {
      this.refs.emptyState.hidden = filtered.length > 0;
    }

    if (this.refs.loadMore) {
      this.refs.loadMore.hidden = this.#visibleCount >= filtered.length;
    }
  }
}

if (!customElements.get('customer-reviews-component')) {
  customElements.define('customer-reviews-component', CustomerReviews);
}
